import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { getFullName } from '@frontend/name';
import { ScheduleRequestListItem, useScheduleRequestType } from '@frontend/schedule-helpers';
import { useModalControl } from '@frontend/design-system';
import { useCheckPaymentsEnabled } from '../../../../../../../hooks/use-check-payments-enabled';
import type { ScheduleRequestActionCalledFrom } from '../../hooks/use-schedule-request-actions';
import type { ScheduleListItemVariant } from '../components/schedule-request-item-header-ui';
import { PreferenceBasedScheduleRequestItemHeader } from './preference-based-item-header';
import { ScheduleRequestListItemHeader } from './schedule-request-item-header';

export type ScheduleRequestItemHeaderProps = {
  variant?: ScheduleListItemVariant;
  listItem: ScheduleRequestListItem;
  setSelectedScheduleRequest: (request: ScheduleRequest | null) => void;
  scheduleRequestModalProps: ReturnType<typeof useModalControl>;
  locationChip?: (locationName: string) => React.ReactNode;
  patientContactedAt?: string;
  deleteRequestModalProps: ReturnType<typeof useModalControl>;
  dismissRequestModalProps: ReturnType<typeof useModalControl>;
  rejectRequestModalProps: ReturnType<typeof useModalControl>;
  onCompleteRequest: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onDismissRequestIntent: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onMessage: (updateStatus: boolean, actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  onPhoneCall: (actionCalledFrom?: ScheduleRequestActionCalledFrom) => void;
  moreInfo?: React.ReactNode;
};

export const ScheduleRequestItemHeader = ({
  variant,
  listItem,
  setSelectedScheduleRequest,
  scheduleRequestModalProps,
  locationChip,
  patientContactedAt,
  deleteRequestModalProps,
  dismissRequestModalProps,
  rejectRequestModalProps,
  onCompleteRequest,
  onDismissRequestIntent,
  onMessage,
  onPhoneCall,
  moreInfo,
}: ScheduleRequestItemHeaderProps) => {
  const { isPaymentsEnabled } = useCheckPaymentsEnabled(listItem.locationId);

  const { isAiRequest, isPreferenceBasedRequest, isUrgentRequest, requestSource, isV3Request } =
    useScheduleRequestType(listItem);
  const sharedProps = {
    requestVersion: listItem.linkHash || '',
    name: getFullName(listItem.schedulee ?? {}),
    appointmentTime: listItem?.dateTime ?? '',
    personPhone: listItem?.schedulee?.phoneNumber ?? '',
    locationId: listItem.locationId ?? '',
    scheduleRequestId: listItem.id ?? '',
    variant,
    locationChip,
    requestSource,
    moreInfo,
  };

  const updateSelectedScheduleRequest = () => {
    if (isAiRequest) {
      // TODO: Handle widget action
    } else {
      setSelectedScheduleRequest(listItem);
      scheduleRequestModalProps.openModal();
    }
  };

  return isPreferenceBasedRequest ? (
    <PreferenceBasedScheduleRequestItemHeader
      {...sharedProps}
      isAiRequest={isAiRequest}
      isUrgentRequest={isUrgentRequest}
      patientContactedAt={patientContactedAt}
      deleteRequestModalProps={deleteRequestModalProps}
      dismissRequestModalProps={dismissRequestModalProps}
      onCompleteRequest={onCompleteRequest}
      onDismissRequestIntent={onDismissRequestIntent}
      onMessage={onMessage}
      onPhoneCall={onPhoneCall}
      isV3Request={isV3Request}
    />
  ) : (
    <ScheduleRequestListItemHeader
      {...sharedProps}
      paidAmount={isPaymentsEnabled && listItem?.bookingAmount ? listItem?.bookingAmount : 0}
      updateSelectedScheduleRequest={updateSelectedScheduleRequest}
      deleteRequestModalProps={deleteRequestModalProps}
      rejectRequestModalProps={rejectRequestModalProps}
      dismissRequestModalProps={dismissRequestModalProps}
      onMessage={onMessage}
      isV3Request={isV3Request}
    />
  );
};
